import {
  Accessor,
  createContext,
  createMemo,
  createSignal,
  JSXElement,
  ParentComponent,
  Setter,
  untrack,
  useContext,
} from 'solid-js';
import { VolumetricMeasurementDefinition } from './types/measurement-definition';
import { VolumetricMeasurementService } from './volumetric-measurement-service';
import { createStore, SetStoreFunction } from 'solid-js/store';
import { BoundariesResponse } from './types/boundary.types';
import { ExclusionsResponse } from './types/exclusion.types';
import { t } from '@trimble/i18n';
import {
  boundaryInclusiveToSnapPicks,
  getNextVolumeLabel,
  MeasurementsCapabilityProvider,
  VolumeMeasurementType,
} from '@trimble/web3d-providers';
import { Vector3 } from '@technology/web3d';
import { isValidXorSide } from './build-volume-measurement-parameters';

export type MeasurementState = 'configure' | 'computed' | 'saved';

interface VolumetricMeasurementsContextValue {
  state: Accessor<MeasurementState>;
  setState: Setter<MeasurementState>;
  measurementsProvider: Accessor<MeasurementsCapabilityProvider>;
  measurementConfig: VolumetricMeasurementDefinition;
  setMeasurementConfig: SetStoreFunction<VolumetricMeasurementDefinition>;
  isValidConfig: Accessor<boolean>;
  boundaries: Accessor<BoundariesResponse>;
  exclusions: Accessor<ExclusionsResponse>;
  measurementError: Accessor<string | undefined>;
  setMeasurementError: Setter<string | undefined>;
  measurement: Accessor<VolumeMeasurementType | undefined>;
}

const VolumetricMeasurementsContext =
  createContext<VolumetricMeasurementsContextValue>();

export interface VolumetricMeasurementProviderProps {
  children?: JSXElement;
  authToken: string;
  accountId: string;
  projectId?: string;
  worksosDomain: string;
  measurementsProvider: MeasurementsCapabilityProvider;
  measurement?: VolumeMeasurementType;
}

function getDefaultVolumeName(
  measurementsProvider: MeasurementsCapabilityProvider
): string {
  return getNextVolumeLabel(measurementsProvider.items() ?? []);
}

export const VolumetricMeasurementsProvider: ParentComponent<
  VolumetricMeasurementProviderProps
> = (props): JSXElement => {
  const initialMeasurement = untrack(() => props.measurement);
  const toSurfaceId = initialMeasurement?.parameters.toSurfaceId ?? undefined;
  const fromSurfaceId =
    initialMeasurement?.parameters.fromSurfaceId ?? undefined;
  const fromFilterId = initialMeasurement?.parameters.fromFilterId ?? undefined;
  const toFilterId = initialMeasurement?.parameters.toFilterId ?? undefined;

  let boundaryData: Vector3[] | undefined = undefined;

  if (initialMeasurement?.parameters.boundaryInclusive) {
    const picks = boundaryInclusiveToSnapPicks(
      initialMeasurement!.parameters.boundaryInclusive
    );
    if (picks) {
      const points = picks
        .map((p) => p.point)
        .filter((p): p is Vector3 => p != null);
      boundaryData = points;
    }
  }

  const [measurementConfig, setMeasurementConfig] =
    createStore<VolumetricMeasurementDefinition>({
      type: initialMeasurement?.parameters.volumeType ?? 'SurfaceToSurface',
      name: initialMeasurement?.label
        ? initialMeasurement.label
        : (untrack(() => getDefaultVolumeName(props.measurementsProvider)) ??
          t('measurements.volumetric.defaultVolumeName')),
      toSurfaceId,
      fromSurfaceId,
      fromFilterId,
      toFilterId,
      boundaryData,
    });

  const getMeasurementConfig = (): VolumetricMeasurementDefinition =>
    measurementConfig;

  const [state, setState] = createSignal<MeasurementState>(
    initialMeasurement !== undefined ? 'saved' : 'configure'
  );
  const [measurementError, setMeasurementError] = createSignal<
    string | undefined
  >(undefined);

  const isValidConfig = (): boolean => {
    const config = getMeasurementConfig();

    if (!config) return false;
    return (
      isValidXorSide(config.fromSurfaceId, config.fromFilterId) &&
      isValidXorSide(config.toSurfaceId, config.toFilterId)
    );
  };

  const volumetricMeasurementService = new VolumetricMeasurementService();

  const boundariesQuery = volumetricMeasurementService.getBoundaries(
    untrack(() => props.projectId!)
  );
  const boundaries = createMemo(() => {
    return boundariesQuery.data ?? { boundaries: [] };
  });

  const exclusionsQuery = volumetricMeasurementService.getExclusions(
    untrack(() => props.projectId!)
  );
  const exclusions = createMemo(() => {
    return exclusionsQuery.data ?? { exclusions: [] };
  });

  return (
    <VolumetricMeasurementsContext.Provider
      value={{
        state,
        setState,
        measurementsProvider: () => props.measurementsProvider,
        measurementConfig,
        setMeasurementConfig,
        isValidConfig,
        boundaries,
        exclusions,
        measurementError,
        setMeasurementError,
        measurement: () => props.measurement,
      }}
    >
      {props.children}
    </VolumetricMeasurementsContext.Provider>
  );
};

export function useVolumetricMeasurements(): VolumetricMeasurementsContextValue {
  const context = useContext(VolumetricMeasurementsContext);
  if (!context)
    throw new Error(
      'useVolumetricMeasurements must be used within a VolumetricMeasurementsProvider'
    );
  return context;
}
